import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Calendar, Users, Activity, MapPin, Clock, TrendingUp } from 'lucide-react';

interface UsageStats {
  totalEvents: number;
  uniqueUsers: number;
  mostActiveGarita: string;
  peakHour: string;
}

interface EventByGarita {
  garita: string;
  count: number;
}

interface EventByType {
  event_type: string;
  count: number;
}

interface TopUser {
  user_id: string;
  event_count: number;
  user_email?: string;
}

interface HourlyActivity {
  hour: string;
  count: number;
}

interface DailyActivity {
  date: string;
  count: number;
}

const UsageAnalytics = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [stats, setStats] = useState<UsageStats>({
    totalEvents: 0,
    uniqueUsers: 0,
    mostActiveGarita: '',
    peakHour: ''
  });
  const [eventsByGarita, setEventsByGarita] = useState<EventByGarita[]>([]);
  const [eventsByType, setEventsByType] = useState<EventByType[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [hourlyActivity, setHourlyActivity] = useState<HourlyActivity[]>([]);
  const [dailyActivity, setDailyActivity] = useState<DailyActivity[]>([]);

  const isSpanish = language === 'es';

  const translations = {
    es: {
      title: 'Analítica de Uso',
      description: 'Estadísticas de actividad y uso de la aplicación',
      overview: 'Resumen',
      eventsByLocation: 'Eventos por Garita',
      eventsByType: 'Eventos por Tipo',
      topUsers: 'Usuarios Más Activos',
      timeAnalysis: 'Análisis Temporal',
      totalEvents: 'Total de Eventos',
      uniqueUsers: 'Usuarios Únicos',
      mostActiveLocation: 'Garita Más Activa',
      peakHour: 'Hora Pico',
      last7Days: 'Últimos 7 días',
      last30Days: 'Últimos 30 días',
      last90Days: 'Últimos 90 días',
      allTime: 'Todo el tiempo',
      hourlyActivity: 'Actividad por Hora',
      dailyActivity: 'Actividad Diaria',
      events: 'eventos',
      users: 'usuarios',
      hour: 'Hora',
      date: 'Fecha',
      count: 'Cantidad',
      eventType: 'Tipo de Evento',
      location: 'Garita',
      noData: 'No hay datos disponibles',
      checkInStarted: 'Check-in Iniciado',
      checkOutCompleted: 'Check-out Completado',
      photoUploaded: 'Foto Subida',
      pointsEarned: 'Puntos Ganados',
      chatOpened: 'Chat Abierto',
      adClicked: 'Anuncio Clickeado',
      borderSelected: 'Frontera Seleccionada',
      languageSelected: 'Idioma Seleccionado'
    },
    en: {
      title: 'Usage Analytics',
      description: 'Application activity and usage statistics',
      overview: 'Overview',
      eventsByLocation: 'Events by Border',
      eventsByType: 'Events by Type',
      topUsers: 'Most Active Users',
      timeAnalysis: 'Time Analysis',
      totalEvents: 'Total Events',
      uniqueUsers: 'Unique Users',
      mostActiveLocation: 'Most Active Border',
      peakHour: 'Peak Hour',
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
      last90Days: 'Last 90 days',
      allTime: 'All time',
      hourlyActivity: 'Hourly Activity',
      dailyActivity: 'Daily Activity',
      events: 'events',
      users: 'users',
      hour: 'Hour',
      date: 'Date',
      count: 'Count',
      eventType: 'Event Type',
      location: 'Border',
      noData: 'No data available',
      checkInStarted: 'Check-in Started',
      checkOutCompleted: 'Check-out Completed',
      photoUploaded: 'Photo Uploaded',
      pointsEarned: 'Points Earned',
      chatOpened: 'Chat Opened',
      adClicked: 'Ad Clicked',
      borderSelected: 'Border Selected',
      languageSelected: 'Language Selected'
    }
  };

  const t = (key: keyof typeof translations.es) => translations[isSpanish ? 'es' : 'en'][key];

  const getTimeRangeFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return '2020-01-01T00:00:00.000Z';
    }
  };

  const translateEventType = (eventType: string) => {
    const eventTranslations: Record<string, string> = {
      'check_in_started': t('checkInStarted'),
      'check_out_completed': t('checkOutCompleted'),
      'photo_uploaded': t('photoUploaded'),
      'points_earned': t('pointsEarned'),
      'chat_opened': t('chatOpened'),
      'ad_clicked': t('adClicked'),
      'border_selected': t('borderSelected'),
      'language_selected': t('languageSelected')
    };
    return eventTranslations[eventType] || eventType;
  };

  const fetchUsageData = async () => {
    setLoading(true);
    const timeFilter = getTimeRangeFilter();

    try {
      // Basic stats
      const { data: basicStats } = await supabase
        .from('usage_logs')
        .select('*')
        .gte('timestamp', timeFilter);

      if (basicStats) {
        const uniqueUserIds = new Set(basicStats.filter(log => log.user_id).map(log => log.user_id));
        
        // Find most active garita
        const garitaCounts: Record<string, number> = {};
        basicStats.forEach(log => {
          const metadata = log.metadata as any;
          const garita = metadata?.garita || 'Unknown';
          garitaCounts[garita] = (garitaCounts[garita] || 0) + 1;
        });
        const mostActiveGarita = Object.keys(garitaCounts).reduce((a, b) => 
          garitaCounts[a] > garitaCounts[b] ? a : b, 'N/A'
        );

        // Find peak hour
        const hourCounts: Record<string, number> = {};
        basicStats.forEach(log => {
          const hour = new Date(log.timestamp).getHours();
          hourCounts[hour] = (hourCounts[hour] || 0) + 1;
        });
        const peakHour = Object.keys(hourCounts).reduce((a, b) => 
          hourCounts[a] > hourCounts[b] ? a : b, '0'
        );

        setStats({
          totalEvents: basicStats.length,
          uniqueUsers: uniqueUserIds.size,
          mostActiveGarita,
          peakHour: `${peakHour}:00`
        });
      }

      // Events by garita
      const { data: garitaData } = await supabase
        .from('usage_logs')
        .select('metadata')
        .gte('timestamp', timeFilter)
        .not('metadata->garita', 'is', null);

      if (garitaData) {
        const garitaCounts: Record<string, number> = {};
        garitaData.forEach(log => {
          const metadata = log.metadata as any;
          const garita = metadata?.garita || 'Unknown';
          garitaCounts[garita] = (garitaCounts[garita] || 0) + 1;
        });
        setEventsByGarita(
          Object.entries(garitaCounts).map(([garita, count]) => ({ garita, count }))
        );
      }

      // Events by type
      const { data: typeData } = await supabase
        .from('usage_logs')
        .select('event_type')
        .gte('timestamp', timeFilter);

      if (typeData) {
        const typeCounts: Record<string, number> = {};
        typeData.forEach(log => {
          typeCounts[log.event_type] = (typeCounts[log.event_type] || 0) + 1;
        });
        setEventsByType(
          Object.entries(typeCounts).map(([event_type, count]) => ({ event_type, count }))
        );
      }

      // Top users
      const { data: userActivity } = await supabase
        .from('usage_logs')
        .select('user_id')
        .gte('timestamp', timeFilter)
        .not('user_id', 'is', null);

      if (userActivity) {
        const userCounts: Record<string, number> = {};
        userActivity.forEach(log => {
          if (log.user_id) {
            userCounts[log.user_id] = (userCounts[log.user_id] || 0) + 1;
          }
        });
        
        const sortedUsers = Object.entries(userCounts)
          .map(([user_id, event_count]) => ({ user_id, event_count }))
          .sort((a, b) => b.event_count - a.event_count)
          .slice(0, 10);
        
        setTopUsers(sortedUsers);
      }

      // Hourly activity
      const { data: hourlyData } = await supabase
        .from('usage_logs')
        .select('timestamp')
        .gte('timestamp', timeFilter);

      if (hourlyData) {
        const hourCounts: Record<string, number> = {};
        for (let i = 0; i < 24; i++) {
          hourCounts[i.toString().padStart(2, '0')] = 0;
        }
        
        hourlyData.forEach(log => {
          const hour = new Date(log.timestamp).getHours().toString().padStart(2, '0');
          hourCounts[hour]++;
        });
        
        setHourlyActivity(
          Object.entries(hourCounts).map(([hour, count]) => ({ hour: `${hour}:00`, count }))
        );
      }

      // Daily activity
      const { data: dailyData } = await supabase
        .from('usage_logs')
        .select('timestamp')
        .gte('timestamp', timeFilter);

      if (dailyData) {
        const dateCounts: Record<string, number> = {};
        dailyData.forEach(log => {
          const date = new Date(log.timestamp).toISOString().split('T')[0];
          dateCounts[date] = (dateCounts[date] || 0) + 1;
        });
        
        setDailyActivity(
          Object.entries(dateCounts)
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date.localeCompare(b.date))
        );
      }

    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageData();
  }, [timeRange]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('description')}</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">{t('last7Days')}</SelectItem>
            <SelectItem value="30d">{t('last30Days')}</SelectItem>
            <SelectItem value="90d">{t('last90Days')}</SelectItem>
            <SelectItem value="all">{t('allTime')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('totalEvents')}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEvents.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{t('events')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('uniqueUsers')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">{t('users')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('mostActiveLocation')}</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.mostActiveGarita}</div>
            <p className="text-xs text-muted-foreground">{t('location')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('peakHour')}</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.peakHour}</div>
            <p className="text-xs text-muted-foreground">{t('hour')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Data Tables */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
          <TabsTrigger value="locations">{t('eventsByLocation')}</TabsTrigger>
          <TabsTrigger value="users">{t('topUsers')}</TabsTrigger>
          <TabsTrigger value="time">{t('timeAnalysis')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('eventsByType')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {eventsByType.map((item, index) => (
                    <div key={item.event_type} className="flex items-center justify-between p-3 rounded-lg border">
                      <span className="font-medium">{translateEventType(item.event_type)}</span>
                      <Badge variant="outline">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('dailyActivity')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dailyActivity.slice(-7).map((item) => (
                    <div key={item.date} className="flex items-center justify-between p-3 rounded-lg border">
                      <span className="font-medium">{item.date}</span>
                      <Badge variant="outline">{item.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('eventsByLocation')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {eventsByGarita.map((item) => (
                  <div key={item.garita} className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="font-medium">{item.garita}</span>
                    <Badge variant="outline">{item.count} {t('events')}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('topUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topUsers.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">{t('noData')}</p>
                ) : (
                  topUsers.map((user, index) => (
                    <div key={user.user_id} className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">#{index + 1}</Badge>
                        <div>
                          <p className="font-medium">{user.user_id.slice(0, 8)}...</p>
                        </div>
                      </div>
                      <Badge>{user.event_count} {t('events')}</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('hourlyActivity')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-6 gap-2">
                {hourlyActivity.map((item) => (
                  <div key={item.hour} className="text-center p-3 rounded-lg border">
                    <div className="text-sm font-medium">{item.hour}</div>
                    <div className="text-lg font-bold">{item.count}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UsageAnalytics;